import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { ResourceRowCRD } from "@k8s-dashboard/contracts";
import { useResourceCatalog, useResourceList, useResourceObject, useScope } from "@/api/queries";
import { useDashboardParams } from "@/state/useDashboardParams";
import { Panel } from "@/components/primitives";
import { LoadingState } from "@/components/SectionState";
import { VirtualTable } from "@/components/VirtualTable";
import { useT } from "@/i18n/I18nProvider";
import { ResourceDetailDrawer } from "./ResourceDetailDrawer";
import { scopeFingerprint } from "./resource-list-state";
import { requestErrorMessage, stateFromError, stateNotice, type StateNotice } from "./state";

/**
 * Custom Resource Definitions 탭 (ADR 0065 C1)
 * --------------------------------------------------------------------------
 * **새 엔드포인트가 없습니다.** 기존 목록·상세 경로를 CRD GVR로 부를 뿐이므로
 * "화면 하나 = 요청 하나"(ADR 0002)가 그대로입니다.
 *
 * 행이 말하는 것은 셋입니다 — group·복수형·생성 시각(metadata에서 바로 나옴),
 * 그리고 서버가 discovery로 대조한 **Explorer allowlist 포함 여부**. 미포함이면
 * 운영자가 `resourceExplorer.extraResources`에 붙일 한 줄을 복사할 수 있습니다.
 * **브라우저에서 allowlist를 바꾸는 경로는 만들지 않습니다.**
 *
 * "제공 버전 미확인"(known=false)을 "미포함"으로 뭉개지 않습니다. 설치 중이거나
 * established 전인 CRD가 그 상태이고, 그때 복사할 한 줄은 없습니다.
 */
const PAGE_SIZE = 50;

/** 가상 스크롤 열 폭. head와 body가 별개 `<table>`이므로 양쪽에 같은 colgroup이 갑니다. */
const COLS = ["auto", "220px", "160px", "190px", "300px"];

/**
 * deep link 해소에 허용하는 **최대 페이지 수**입니다.
 *
 * `crd=<name>/<uid>`가 첫 페이지 밖을 가리키면 cursor로 순차 전진해 찾습니다.
 * 무한히 전진하지 않는 것이 요점입니다 — 20페이지 × 50 = 정의 1,000개까지
 * 자동으로 찾고, 그 밖은 "더 보기"로 **사용자가** 이어 갑니다.
 */
const DEEP_LINK_MAX_PAGES = 20;

/** CRD 정의 GVR. 기본 allowlist에 들어 있고 내장 group이라 opt-in이 없습니다. */
const CRD_GVR = { group: "apiextensions.k8s.io", version: "v1", resource: "customresourcedefinitions" } as const;

/**
 * CRD 객체 이름을 `<plural>.<group>`으로 나눕니다.
 *
 * Kubernetes가 CRD 이름을 그 형식으로 **강제**하므로, 행 하나의 group·복수형은
 * 추가 조회 없이 이름만으로 정확합니다. 형식이 아니면 지어내지 않고 빈 값입니다 —
 * 이름 전체를 group에 넣으면 운영자가 잘못된 값을 복사합니다.
 */
function crdNameParts(name: string): { plural: string; group: string } {
  const at = name.indexOf(".");
  if (at <= 0 || at === name.length - 1) return { plural: "", group: "" };
  return { plural: name.slice(0, at), group: name.slice(at + 1) };
}

export function CRDCatalog() {
  const [params, setParams] = useSearchParams();
  const { clusterId } = useDashboardParams();
  const scope = useScope();
  const { t } = useT("resources");
  /* 로딩 문구는 **이미 있는** common 조각을 씁니다 — 같은 말을 조각마다 다시
     번역하지 않습니다. common은 부트 이름공간이라 추가 chunk가 없습니다. */
  const { t: tc } = useT("common");
  const available = scope.data?.canExploreResources ?? false;

  /* 카탈로그는 "이 배포가 CRD 정의를 조회 대상으로 두는가"의 유일한 근거입니다.
     **카탈로그 자체가 실패했을 때 그것을 allowlist 누락으로 읽지 않습니다** —
     차가운 500은 upstream 장애이고, "설정되지 않았다"와 다른 사실입니다. */
  const catalog = useResourceCatalog(clusterId, available);
  const catalogLoaded = catalog.data !== undefined;
  const descriptor = useMemo(
    () =>
      (catalog.data?.items ?? []).find(
        (d) => d.group === CRD_GVR.group && d.version === CRD_GVR.version && d.resource === CRD_GVR.resource,
      ),
    [catalog.data],
  );
  const allowlisted = Boolean(descriptor);

  const list = useResourceList(
    {
      clusterId,
      ...CRD_GVR,
      namespaced: descriptor?.namespaced ?? true,
      /* 클러스터 범위 리소스입니다 — ns를 붙여 보내면 서버가 400으로 거절합니다. */
      namespace: "all",
      namePrefix: "",
      labelSelector: "",
      order: "asc",
      limit: PAGE_SIZE,
      preferencePartition: scope.data?.preferencePartition ?? "",
      scopeFingerprint: scopeFingerprint(scope.data, clusterId),
      maxPages: DEEP_LINK_MAX_PAGES,
    },
    available && allowlisted,
  );

  const pages = list.data?.pages ?? [];
  const rows = useMemo(() => pages.flatMap((p) => p.items), [list.data]);
  const total = pages[0]?.total ?? 0;

  /* 선택은 URL에 둡니다 — 새로고침·공유에 견디고, 갱신이 지우지 않습니다. */
  const item = params.get("crd") ?? "";
  const selected = rows.find((r) => `${r.name}/${r.uid}` === item);

  /**
   * deep link 해소 — **유계 순차 전진**이고 재시도 루프가 없습니다.
   *
   * 새로고침이나 공유 링크가 첫 페이지 밖의 정의를 가리키면, 서버 cursor로
   * 한 페이지씩 이어 불러 찾습니다. 멈추는 조건이 셋입니다.
   *
   *   1. `DEEP_LINK_MAX_PAGES`(20페이지 · 정의 1,000개)까지만 전진합니다.
   *   2. **cursor 하나는 한 번만 시도합니다.** 이 가드가 없으면 다음 페이지
   *      조회가 500으로 계속 실패할 때 `pages.length`·`hasNextPage`가 그대로인
   *      채 `isFetchingNextPage`만 true→false로 깜빡여 effect가 다시 돌고,
   *      그것이 곧 **무한 자동 재시도**(서버를 때리는 루프)입니다.
   *   3. 다음 페이지 조회가 실패하면(`isFetchNextPageError`) 자동 전진을 멈춥니다.
   *
   * 멈춘 뒤에도 조용히 실패하지 않습니다 — "더 보기"가 **사용자의 명시적 재시도**로
   * 남습니다. 요청은 전부 서버 페이지 조회이고 Kubernetes 호출은 0회입니다.
   */
  const nextCursor = pages[pages.length - 1]?.nextCursor ?? "";
  /* 시도 기록은 **선택에 묶입니다.** 선택이 바뀌면 처음부터 다시 찾을 수 있어야
     하는데, 초기화를 effect로 두면 그 effect가 돌 때 이미 같은 렌더의 판정이
     끝나 있어 새 선택의 첫 전진이 한 박자 늦거나 아예 일어나지 않습니다.
     그래서 렌더 중에 파생 캐시를 맞춥니다 — 외부 효과가 없고 멱등합니다. */
  const attempted = useRef<{ item: string; cursors: Set<string> }>({ item, cursors: new Set() });
  if (attempted.current.item !== item) attempted.current = { item, cursors: new Set() };
  const canAdvance =
    Boolean(item) &&
    !selected &&
    list.hasNextPage &&
    !list.isFetchNextPageError &&
    pages.length < DEEP_LINK_MAX_PAGES &&
    Boolean(nextCursor) &&
    !attempted.current.cursors.has(nextCursor);
  useEffect(() => {
    /* `item`이 의존성에 **있어야** 합니다. 선택이 바뀌어도 `canAdvance`와
       `nextCursor`가 그대로일 수 있고(같은 페이지에서 다른 항목을 deep link),
       그러면 의존성이 변하지 않아 새 선택의 해소가 시작되지 않습니다.
       본문의 재확인은 `isFetchingNextPage` 깜빡임에 호출이 따라붙지 않게 합니다. */
    if (!canAdvance || list.isFetchingNextPage) return;
    if (attempted.current.cursors.has(nextCursor)) return;
    attempted.current.cursors.add(nextCursor);
    void list.fetchNextPage();
  }, [item, canAdvance, nextCursor, list.isFetchingNextPage, list.fetchNextPage]);
  /* 전진 중인지는 **시도한 cursor가 돌아오는 중인지**로 봅니다 — 깜빡임에
     기대지 않습니다. */
  const resolving = Boolean(item) && !selected && (canAdvance || list.isFetchingNextPage);
  /* 해소가 끝났는데 못 찾았다면 그 사실을 말합니다. "더 보기"가 남아 있으면
     이어 갈 수 있고, 없으면 목록에 없는 항목입니다. */
  const unresolved = Boolean(item) && !selected && !resolving && list.isSuccess;

  const target = selected ? { namespace: "", name: selected.name, uid: selected.uid } : null;
  const object = useResourceObject(clusterId, CRD_GVR, target);

  const select = (value: string) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (value) p.set("crd", value);
        else p.delete("crd");
        return p;
      },
      { replace: true },
    );

  /* 기능 자체가 없는 배포(central 모드 · 스위치 꺼짐)와 Scope 거절은 **allowlist
     누락과 다른 사실입니다.** Explorer와 같은 `state.ts` 문구를 그대로 씁니다 —
     "조회 대상이 아니다"(allowlist)와 "쓸 수 없다"(기능 없음)를 섞지 않습니다. */
  if (!available || stateFromError(catalog.error) === "unavailable") {
    return (
      <Panel title={t("resources.title")} subtitle={t("resources.subtitle", { count: 0 })}>
        <StateBox notice={stateNotice("unavailable")} />
      </Panel>
    );
  }

  /* 표시 상태는 `state.ts`가 소유합니다 — 서버가 이미 구분해 준 것을 UI가
     지우지 않습니다. 순서가 계약입니다: 카탈로그 장애 → allowlist 누락 →
     목록 상태 → 평범한 오류 → 0건. */
  const catalogState = stateFromError(catalog.error);
  const catalogMessage = requestErrorMessage(catalog.error);
  const listState = stateFromError(list.error);
  const listMessage = requestErrorMessage(list.error);

  return (
    <>
      <Panel
        title={t("resources.title")}
        subtitle={t("resources.subtitle", { count: total || rows.length })}
        actions={
          allowlisted ? (
            <button type="button" className="linkish" onClick={() => void list.refetch()} disabled={list.isFetching}>
              {t("resources.reload")}
            </button>
          ) : undefined
        }
        flush
      >
        {catalog.isLoading || (allowlisted && list.isLoading && rows.length === 0) ? (
          <LoadingState />
        ) : catalogState ? (
          /* 카탈로그 상태 장애(503 unavailable·syncing 등)는 **allowlist 판정보다
             먼저**입니다. 카탈로그를 읽지 못했으면 "대상이 아니다"를 말할 근거가 없습니다. */
          <StateBox notice={stateNotice(catalogState)} />
        ) : catalogMessage ? (
          /* 매핑되지 않는 카탈로그 오류(차가운 500 upstream_unavailable 등)도
             allowlist 누락으로 읽지 않습니다 — upstream 장애를 그대로 말합니다. */
          <div className="state state--error" role="alert">
            <span className="state__glyph" aria-hidden="true">
              ✕
            </span>
            <span className="state__title">{t("resources.err")}</span>
            <span className="state__detail">{catalogMessage}</span>
          </div>
        ) : !catalogLoaded ? (
          <LoadingState />
        ) : !allowlisted ? (
          <Unavailable tone="warning" />
        ) : listState ? (
          /* forbidden·syncing·unsupported·missing은 **성공-0건으로 뭉개지 않습니다.** */
          <StateBox notice={stateNotice(listState, descriptor?.reason)} />
        ) : listMessage ? (
          <div className="state state--error" role="alert">
            <span className="state__glyph" aria-hidden="true">
              ✕
            </span>
            <span className="state__title">{t("resources.err")}</span>
            <span className="state__detail">{listMessage}</span>
          </div>
        ) : rows.length === 0 ? (
          <div className="state">
            <span className="state__glyph" aria-hidden="true">
              ✓
            </span>
            {/* 제목이 이미 사실입니다 — 같은 말을 설명으로 되풀이하지 않습니다. */}
            <span className="state__title">{t("resources.none")}</span>
          </div>
        ) : (
          <>
            {/* deep link가 아직 해소되지 않았으면 그 사실과 다음 행동을 말합니다. */}
            {resolving && <LoadingState />}
            {unresolved && (
              <div className="state state--warning resource-columns__notice" role="note">
                <span className="state__glyph" aria-hidden="true">
                  ▲
                </span>
                <span className="state__title">
                  {list.hasNextPage ? t("resources.unresolved") : t("resources.gone")}
                </span>
                <span className="state__detail">{item}</span>
              </div>
            )}
            {/* **렌더 행은 뷰포트 + 여유분으로 유계입니다**(가상 스크롤, 이슈 #15).
                정의가 2,000개여도 DOM 행은 수십 개입니다. */}
            <VirtualTable
              items={rows}
              rowHeight={34}
              height={440}
              columns={COLS}
              getKey={(row) => row.uid}
              header={
                <tr>
                  {/* `scope="col"`을 붙입니다. 실측 근거: Playwright의 역할 해석
                      (`injectedScriptSource` TH 분기)은 `scope`가 없으면 `th`를
                      `cell`로 돌려줘 역할 로케이터가 머리글을 찾지 못했습니다
                      (측정 당시 columnheader 0개). 이 저장소의 다른 표
                      (MetricInvestigationCard · RolloutCompareCard)도 같은 속성을 씁니다. */}
                  <th scope="col">{t("resources.col.name")}</th>
                  <th scope="col">{t("resources.col.group")}</th>
                  <th scope="col">{t("resources.col.plural")}</th>
                  <th scope="col">{t("resources.col.created")}</th>
                  <th scope="col">{t("resources.col.in")}</th>
                </tr>
              }
              renderRow={(row) => {
                /* group·복수형은 **이름에서** 나옵니다 — CRD 객체 이름이
                   `<plural>.<group>`이라는 Kubernetes 규칙이 그대로 근거입니다
                   (추가 조회 0회). */
                const parts = crdNameParts(row.name);
                return (
                  <>
                    <td className="ds-ident">
                      <button type="button" className="linkish" onClick={() => select(`${row.name}/${row.uid}`)}>
                        {row.name}
                      </button>
                      {/* Application CRD는 generic instance 목록을 자동 조회하지 않습니다.
                          서버 capability가 있을 때만 전용 read-only 화면으로 이동하는 CTA입니다. */}
                      {row.name === "applications.argoproj.io" && scope.data?.canViewGitOps === true && (
                        <Link className="linkish" to="/gitops">{t("resources.gitops.open")}</Link>
                      )}
                    </td>
                    <td className="ds-ident">{parts.group || "-"}</td>
                    <td className="ds-ident">{parts.plural || "-"}</td>
                    <td>{row.createdAt ?? "-"}</td>
                    <td>
                      <ExplorerMembership crd={row.crd} />
                    </td>
                  </>
                );
              }}
            />
            <div className="resource-list__footer">
              <span className="muted">
                {t("resources.shown", { shown: rows.length })}
                {list.hasNextPage ? t("resources.next") : ""}
              </span>
              {list.hasNextPage && (
                <button
                  type="button"
                  className="ds-button ds-button--sm ds-button--ghost"
                  onClick={() => void list.fetchNextPage()}
                  disabled={list.isFetchingNextPage}
                >
                  {list.isFetchingNextPage ? tc("common.state.loading") : t("resources.more")}
                </button>
              )}
            </div>
          </>
        )}
      </Panel>

      <ResourceDetailDrawer
        open={Boolean(target)}
        loading={object.isLoading}
        detail={object.data}
        error={object.error}
        onClose={() => select("")}
        /* 정의는 **조회 전용**입니다. CRD는 설정으로 뚫을 수 없는 hard-deny이므로
           검토·적용·삭제 진입점을 만들지 않습니다(ADR 0019 · ADR 0065 C1 결정 5). */
      />
    </>
  );
}

/**
 * `state.ts`가 만든 알림 하나. 문구·톤·글리프를 여기서 지어내지 않습니다 —
 * Explorer와 **같은 표**를 씁니다(새 i18n 조각이 늘지 않습니다).
 */
function StateBox({ notice }: { notice: StateNotice }) {
  const cls = notice.tone === "neutral" ? "state" : `state state--${notice.tone}`;
  return (
    <div
      className={cls}
      role={notice.tone === "forbidden" || notice.tone === "error" ? "alert" : "note"}
      data-state={notice.state}
    >
      <span className="state__glyph" aria-hidden="true">
        {notice.tone === "neutral" ? "✓" : notice.tone === "forbidden" ? "!" : "▲"}
      </span>
      <span className="state__title">{notice.title}</span>
      <span className="state__detail">{notice.detail}</span>
    </div>
  );
}

/**
 * "조회 대상이 아니다"는 **한 문장**입니다. 권한이 없어서(forbidden)와 allowlist에
 * 없어서(warning)는 톤만 다르므로 마크업을 두 벌로 두지 않습니다.
 */
function Unavailable({ tone }: { tone: "forbidden" | "warning" }) {
  const { t } = useT("resources");
  return (
    <div className={`state state--${tone}`} role={tone === "forbidden" ? "alert" : "note"}>
      <span className="state__glyph" aria-hidden="true">
        {tone === "forbidden" ? "!" : "▲"}
      </span>
      <span className="state__title">{t("resources.off")}</span>
      <span className="state__detail">{t("resources.off.d")}</span>
    </div>
  );
}

/**
 * Explorer 포함 여부. **세 상태가 서로 다른 사실입니다** —
 * 포함됨 / 미포함(복사할 한 줄 있음) / 제공 버전 미확인(단정하지 않음).
 * 색만으로 구분하지 않고 언제나 텍스트 라벨을 함께 씁니다(ADR 0001).
 *
 * 분기를 늘리는 대신 **표 한 벌**로 그립니다 — 마크업이 세 벌로 늘어나면
 * 한 곳만 고쳐져 상태마다 다른 접근성 모양이 됩니다.
 */
const MEMBERSHIP = {
  included: ["✓", "resources.in.yes"],
  excluded: ["▲", "resources.in.no"],
  unknown: ["?", "resources.in.unknown"],
} as const;

function ExplorerMembership({ crd }: { crd?: ResourceRowCRD }) {
  const { t } = useT("resources");
  const state: keyof typeof MEMBERSHIP = !crd || !crd.known ? "unknown" : crd.allowlisted ? "included" : "excluded";
  const [glyph, labelId] = MEMBERSHIP[state];
  return (
    <span className="resource-crd__state" data-state={state}>
      <span className="resource-crd__glyph" aria-hidden="true">
        {glyph}
      </span>
      {/* 라벨 자체가 상태입니다 — 표 한 칸에 문단을 넣지 않습니다. 긴 설명은
          정의 상세의 Explorer 절에 있습니다(같은 i18n 문장을 씁니다). */}
      <span className="resource-crd__label">{t(labelId)}</span>
      {state === "excluded" && crd?.suggestedEntry && <CopyEntry entry={crd.suggestedEntry} />}
    </span>
  );
}

/**
 * `extraResources` 한 줄 복사.
 *
 * 값은 **언제나 화면에 보입니다** — 클립보드가 막힌 환경(비보안 컨텍스트·권한
 * 거부)에서도 운영자가 직접 선택해 복사할 수 있어야 하기 때문입니다. 실패는
 * 조용히 넘기지 않고 그 사실을 말합니다.
 */
function CopyEntry({ entry }: { entry: string }) {
  const { t } = useT("resources");
  const [status, setStatus] = useState<"idle" | "done" | "failed">("idle");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(entry);
      setStatus("done");
    } catch {
      setStatus("failed");
    }
  };
  return (
    <span className="resource-crd__copy">
      <code className="ds-ident">{entry}</code>
      <button
        type="button"
        className="ds-button ds-button--sm ds-button--ghost"
        aria-label={t("resources.copy.aria", { gvr: entry })}
        onClick={() => void copy()}
      >
        {t("resources.copy")}
      </button>
      {/* 결과는 보조 기술에도 알립니다 — 버튼 라벨만 바꾸면 조용히 지나갑니다. */}
      <span className="resource-crd__copy-status" role="status" data-status={status}>
        {status === "done" ? t("resources.copy.ok") : status === "failed" ? t("resources.copy.err") : ""}
      </span>
    </span>
  );
}
